import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ViewMode } from 'gantt-task-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlassPlus, faMagnifyingGlassMinus, faCalendarDay } from '@fortawesome/free-solid-svg-icons'
import { HTTP_CODES } from 'constants/constants'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { Message, MessageSeverity } from 'constants/message'
import ComponentsUNames from 'constants/componentsUNames'
import { IconButton, Inline, Pagination } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import MessageContext from 'context/MessageContext'
import LoadingContext from 'context/LoadingContext'
import LocalStorage from 'utils/myLocalStorage'
import Utils from 'constants/config'
import UrlApis from 'utils/api/urlApis'
import AuthContext from 'context/AuthContext'
import { useSearchParams } from 'react-router-dom'
import Planning from 'pages/WorkPackage/components/widgets/WidgetPlanning/planning'

/* Gantt diagram properties */
const GANTT_HEADER_HEIGHT = 26
const ZOOM_BAR_HEIGHT = 25
/* ..background-colors for selected task ('fake-duration'-task with no end-date vs. normal task) */
const BG_COLOR_FAKE_SELECTED_WP = '#636363'
const BG_COLOR_FAKE_UNSELECTED_WP = '#a3a3a3'
const BG_COLOR_SELECTED_WP = 'rgb(0, 33, 131)'
const BG_COLOR_UNSELECTED_WP = '#3043eb'
/* zoom-scales matching the keys in ViewMode we want to use for our zooming possibilities (defaulted to Day) */
const zoomScales = ['Day', 'Week', 'Month', 'Year']
const ZOOMING_DEFAULT = 0
const ID_ZOOM_PLUS = 'planning_zoom_plus'
const ID_ZOOM_MINUS = 'planning_zoom_minus'
/* Default duration for work-packages having no datlim nor duration */
const WP_DURATION_IN_DAYS_DEFAULT = 1

const WidgetPlanning = ({ pageUniqueName, bdvnumdoc, handleBDVChange }) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const aircraft = searchParams.get('search')

  const [dateToShow, setDateToShow] = useState(undefined)
  const ganttHeight = 100
  const [zoomingPointer, setZoomingPointer] = useState(ZOOMING_DEFAULT)
  const [isZoomPlusActive, setIsZoomPlusActive] = useState(zoomingPointer > 0)
  const [isZoomMinusActive, setIsZoomMinusActive] = useState(zoomingPointer < zoomScales.length - 1)
  const [data, setData] = useState()
  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.BDV_STORE_NAME,
        ComponentsUNames.BDV_WDG_PLANNING_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      Utils.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0,
    nbPages: 0,
    choiceNbRows: [5, 10, 20, 50],
  })
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)
  // ..in case of auto-click (resource from link), we must bypass te Gantt-engine and proceed manually => we keep track of it in order to undo manually the auto-selection

  const { lang } = useContext(LangContext)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress } = useContext(LoadingContext)
  const { handleRequestLogin } = useContext(AuthContext)

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    try {
      handleLoadInProgress(true)
      setIsLoadInProgressActive(true)
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(
          UrlApis.URL_WPSTATUS_PLANNING_GET +
            '?lang=' +
            lang +
            '&acToFind=' +
            (aircraft || '') +
            '&nbMonths=' +
            eval('CONFIGAPP__NBMONTHS_FOR_DATE_INTERVALS') +
            '&size=' +
            pagination.pageSize +
            '&page=' +
            pagination.pageNo,
        ),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Sync widget's data
      let result = await mapWorkPackages(response?.data.content)
      setPagination({
        ...pagination,
        nbItems: response?.data.totalElements,
        nbPages: response?.data.totalPages,
        pageNo: response?.data.number,
      })

      const selectedWp = result?.find((d) => d.name === bdvnumdoc && d.project === aircraft)
      if (selectedWp) setDateToShow(selectedWp.start)

      setData(result)

      return result
    } catch (err) {
      console.debug('ERR <' + t('7001712') + '>: ' + err)
      if (err.response.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('7001712'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('7001712'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('7001712'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      handleLoadInProgress(false)
      setIsLoadInProgressActive(false)
    }
  }

  const getImgSrc = async (tasks) => {
    const applicabilities = [...new Set(tasks.map((task) => task.applicability))]
    const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)

    try {
      const imgs = await Promise.all(
        applicabilities.map(async (applicability) => {
          return axios.get(
            encodeURI(UrlApis.URL_COMMON_IMAGES_GET + '?aircraftApplicability=' + applicability),
            {},
            {
              headers: headerCompleted,
              withCredentials: false,
            },
          )
        }),
      )
      return applicabilities.map((applicability, i) => ({ applicability, imgSrc: imgs[i].data }))
    } catch (err) {
      console.debug('ERR Workpackage Planning / loading image : ' + err)
    }
  }

  /**
   * Convert received data into Work-Packages data to be shown within the planning
   * @param {*} rawData
   * @returns
   */
  const mapWorkPackages = async (rawData) => {
    let mappedAircrafts = []
    const imgs = await getImgSrc(rawData)
    if (rawData && JSON.stringify(rawData) !== '{}' && JSON.stringify(rawData) !== '[]') {
      for (let i = 0; i < rawData.length; i++) {
        const objAircraft = rawData[i]
        const aircraft = objAircraft.aircraft

        let wpsOfAircraft = []
        for (let j = 0; j < objAircraft.wp.length; j++) {
          const wp = objAircraft.wp[j]

          const beginDate = wp.bdvdatmin || wp.bdvdatouv

          if (beginDate) {
            const startDate = new Date(
              parseInt(beginDate.substring(6, 10)),
              parseInt(beginDate.substring(3, 5)) - 1,
              parseInt(beginDate.substring(0, 2)),
            )
            let endDate =
              wp.hasOwnProperty('bdvdatlim') && wp.bdvdatlim !== undefined
                ? new Date(
                    parseInt(wp.bdvdatlim.substring(6, 10)),
                    parseInt(wp.bdvdatlim.substring(3, 5)) - 1,
                    parseInt(wp.bdvdatlim.substring(0, 2)),
                  )
                : null
            let isEndDateFake = false
            if (endDate === null) {
              // We add a duration at start date
              endDate = new Date(startDate)
              // ..we first check for the duration and compute end date with it
              if (wp.hasOwnProperty('vsday') && wp.vsday > 0) {
                endDate.setDate(startDate.getDate() + wp.vsday)
              } else {
                // Fake end date with a default duration
                endDate.setDate(startDate.getDate() + WP_DURATION_IN_DAYS_DEFAULT)
                isEndDateFake = true
              }
            } else {
              // Adjust end date with 1 day more since time is set to 0 in actual version with day only and no time attached to dates
              endDate.setDate(endDate.getDate() + 1)
              // Create a fake date if inconsistency between start and end dates
              if (endDate <= startDate) {
                endDate = new Date(startDate)
                endDate.setDate(startDate.getDate() + WP_DURATION_IN_DAYS_DEFAULT)
                isEndDateFake = true
              }
            }

            const bdv = {
              index: i,
              project: aircraft,
              applicability: objAircraft.applicability,
              name: wp.bdvnumdoc,
              id: wp.bdvnumdoc + '_' + aircraft,
              title: wp.bdvtitle,
              type: 'task',
              isDisabled: false,
              start: startDate,
              end: endDate,
              progress: 0,
              isEndDateFake: isEndDateFake,
              imgSrc: imgs.find(({ applicability }) => applicability === objAircraft.applicability).imgSrc,
              styles: isEndDateFake
                ? {
                    backgroundColor: BG_COLOR_FAKE_UNSELECTED_WP,
                    backgroundSelectedColor: BG_COLOR_FAKE_SELECTED_WP,
                    progressColor: BG_COLOR_FAKE_UNSELECTED_WP,
                    progressSelectedColor: BG_COLOR_FAKE_SELECTED_WP,
                  }
                : {
                    backgroundColor: BG_COLOR_UNSELECTED_WP,
                    backgroundSelectedColor: BG_COLOR_SELECTED_WP,
                    progressColor: BG_COLOR_UNSELECTED_WP,
                    progressSelectedColor: BG_COLOR_SELECTED_WP,
                  },
            }
            wpsOfAircraft.push(bdv)
          }
        }
        // For current aircraft, sort WPs by ascending start-date
        const wpsSortedAndCleanedUp = wpsOfAircraft.sort((a, b) => (a.start > b.start ? 1 : -1))
        // Once sorted, remove the name of the aircraft for WPs other than the first which is displayed (so its name will not appear in the planning)
        wpsSortedAndCleanedUp.forEach((wp, indx) => {
          if (indx > 0) {
            wp.name = wp.name.replace(aircraft, '')
          }
        })
        mappedAircrafts.push(...wpsSortedAndCleanedUp)
      }
    }
    return mappedAircrafts
  }

  const handleSelectionWP = (e) => {
    handleBDVChange(e.name, e.project)
    setDateToShow(e.start)
  }

  const adjustZoomPointer = (indice) => {
    if ((zoomingPointer === 0 && indice < 0) || (zoomingPointer === zoomScales.length - 1 && indice > 0)) {
      // No shit since zooming is set at one of the zooming boundaries
      return zoomingPointer
    }
    return zoomingPointer + indice
  }

  const handleZoom = (e) => {
    const id = e.currentTarget.id
    let nextZoomingPointer = zoomingPointer
    switch (id) {
      case ID_ZOOM_PLUS:
        nextZoomingPointer = adjustZoomPointer(-1)
        setZoomingPointer(nextZoomingPointer)
        break
      case ID_ZOOM_MINUS:
        nextZoomingPointer = adjustZoomPointer(+1)
        setZoomingPointer(nextZoomingPointer)
      default:
        break
    }
    // Update states of zooming icons
    setIsZoomPlusActive(nextZoomingPointer > 0)
    setIsZoomMinusActive(nextZoomingPointer < zoomScales.length - 1)
  }

  useEffect(() => {
    loadData()
  }, [lang, pagination.pageSize, pagination.pageNo])

  return (
    <div>
      {data && data.length ? (
        <div>
          <Planning
            tasks={data}
            locale='en'
            viewMode={ViewMode[zoomScales[zoomingPointer]]}
            preStepsCount='3'
            ganttHeight={ganttHeight}
            todayColor='rgba(0, 24,94, 0.3)'
            headerHeight={GANTT_HEADER_HEIGHT}
            rowHeight={22}
            columnWidth={45}
            fontSize='10px'
            onClick={handleSelectionWP}
            viewDate={dateToShow}
          />
          <div>
            <Inline spacing='1-x' align='end'>
              <div id={ID_ZOOM_PLUS} onClick={zoomingPointer === 0 ? () => {} : handleZoom}>
                <IconButton variant='ghost' size='small' disabled={!isZoomPlusActive}>
                  <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                </IconButton>
              </div>
              <div
                id='planning_go_today'
                onClick={() => {
                  setDateToShow(new Date())
                }}
              >
                <IconButton variant='ghost' size='small'>
                  <FontAwesomeIcon icon={faCalendarDay} />
                </IconButton>
              </div>
              <div id={ID_ZOOM_MINUS} onClick={zoomingPointer === zoomScales.length - 1 ? () => {} : handleZoom}>
                <IconButton variant='ghost' size='small' disabled={!isZoomMinusActive}>
                  <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                </IconButton>
              </div>
            </Inline>
          </div>

          <Pagination
            count={pagination.nbItems}
            page={pagination.pageNo + 1}
            onPageChange={(e, newPage) => setPagination({ ...pagination, pageNo: newPage - 1 })}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e, newRowPerPage) => {
              LocalStorage.put(
                [LocalStorage.KEY_PREFS, pageUniqueName, ComponentsUNames.BDV_WDG_PLANNING_UNAME + '|PAGESIZE'],
                newRowPerPage,
              )
              setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
            }}
            rowsPerPageOptions={pagination.choiceNbRows}
            showRowsPerPage
            rowsPerPageLabel={t('entries_per_page')}
          />
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetPlanning
